import * as angular from 'angular';
import {
    DateTimePickerComponentOptions,
    DateTimePickerController,
    DateTimePickerViewValue,
    RuntimePickerViewValue
} from '../custom';
import { PickerService } from 'app/_services/picker.service';
import { UtilService } from 'app/_services/util.service';

class RuntimePickerComponentCtrl implements DateTimePickerController<
    RuntimePickerViewValue,
    DateInterval
> {
    public readonly INITIAL_VIEW_VALUE: RuntimePickerViewValue = {
        start: { date: null, time: null },
        end: { date: null, time: null }
    };

    public ngModelCtrl: angular.INgModelController;

    public timeDisabled: { start: boolean; end: boolean } = {
        start: false,
        end: false
    };

    public dateDisabled: { start: boolean; end: boolean } = {
        start: false,
        end: false
    };

    public preSetDayAhead: boolean;
    public idPrefix: string;

    public readonly ALT_INPUT_FORMATS = ['d!.M!.yyyy', 'd!.M!.yy'];
    public dateTimeFormat: 'YYYY-MM-DDTHH:mmZ';
    public datePickerOptions: { minDate: Date; maxDate: Date; startingDay: 1 };
    public gasDayEffectiveRuntimePreSelection: DateInterval;
    public viewValue: RuntimePickerViewValue;
    public disabled: boolean;
    public minWidth: boolean;
    public preSetDone: boolean;

    constructor(
        private $scope: angular.IScope,
        private moment: moment.MomentStatic,
        private utilService: UtilService,
        private pickerService: PickerService,
        private $element: angular.IRootElementService
    ) {
        this.dateTimeFormat = this.pickerService.DATE_TIME_FORMAT;
        this.gasDayEffectiveRuntimePreSelection =
            this.pickerService.gasDayEffectiveRuntimePreSelection;
        this.datePickerOptions = {
            maxDate: this.pickerService.maxDate,
            minDate: undefined,
            startingDay: 1
        };
        this.idPrefix = this.utilService.generateGuid(1) + '-';
    }

    $onInit(): void {
        this.$scope.$watch(
            () => this.viewValue,
            () => {
                this.ngModelCtrl.$setViewValue(angular.copy(this.viewValue));

                if (!this.preSetDone) {
                    this.ngModelCtrl.$setPristine();
                    this.ngModelCtrl.$setUntouched();
                    this.preSetDone = true;
                }
            },
            true
        );

        this.$scope.$watch(
            () => this.$element[0].hasAttribute('disabled'),
            (hasDisabledAttribute) => (this.disabled = hasDisabledAttribute)
        );

        this.ngModelCtrl.$isEmpty = (
            viewValue: RuntimePickerViewValue
        ): boolean => {
            return angular.equals(viewValue, this.INITIAL_VIEW_VALUE);
        };

        this.ngModelCtrl.$parsers.push(
            (viewValue: RuntimePickerViewValue): DateInterval => {
                if (this.ngModelCtrl.$isEmpty(viewValue)) {
                    return null;
                }

                const parsedModelValue: DateInterval = <DateInterval>{
                    start: this.pickerService.parseViewValue(viewValue.start),
                    end: this.pickerService.parseViewValue(viewValue.end)
                };

                if (
                    this.pickerService.parseViewValue(viewValue.start) >=
                    this.pickerService.parseViewValue(viewValue.end)
                ) {
                    this.viewValue.end.date.setFullYear(
                        this.viewValue.start.date.getFullYear(),
                        this.viewValue.start.date.getMonth(),
                        this.viewValue.start.date.getDate() + 1
                    );
                    this.viewValue.end.date = angular.copy(
                        this.viewValue.end.date
                    );
                }

                if (
                    angular.isUndefined(parsedModelValue.start) ||
                    angular.isUndefined(parsedModelValue.end)
                ) {
                    return undefined;
                }

                return parsedModelValue;
            }
        );

        this.ngModelCtrl.$render = () => {
            this.preSet(this.ngModelCtrl.$modelValue);
        };
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (!angular.isUndefined(onChangesObj['startTimeDisabled']))
            this.timeDisabled.start =
                onChangesObj['startTimeDisabled'].currentValue;
        if (!angular.isUndefined(onChangesObj['endTimeDisabled']))
            this.timeDisabled.end =
                onChangesObj['endTimeDisabled'].currentValue;
        if (!angular.isUndefined(onChangesObj['startDateDisabled']))
            this.dateDisabled.start =
                onChangesObj['startDateDisabled'].currentValue;
        if (!angular.isUndefined(onChangesObj['endDateDisabled']))
            this.dateDisabled.end =
                onChangesObj['endDateDisabled'].currentValue;
        if (!angular.isUndefined(onChangesObj['minDate']))
            this.datePickerOptions.minDate =
                onChangesObj['minDate'].currentValue;
        if (!angular.isUndefined(onChangesObj['maxDate']))
            this.datePickerOptions.maxDate =
                onChangesObj['maxDate'].currentValue;
        if (!angular.isUndefined(onChangesObj['minWidth']))
            this.minWidth = onChangesObj['minWidth'].currentValue;

        if (!angular.isUndefined(onChangesObj['preSetDayAhead'])) {
            this.preSetDayAhead = onChangesObj['preSetDayAhead'].currentValue;
            this.preSet();
        }
    }

    public preSet(modelValueToPreSetTo?: DateInterval) {
        const viewValue = <RuntimePickerViewValue>{};

        const runtimeKeys: (keyof RuntimePickerViewValue)[] = ['start', 'end'];
        const dateTimeKeys: (keyof DateTimePickerViewValue)[] = [
            'date',
            'time'
        ];

        for (let starOrEnd of runtimeKeys) {
            viewValue[starOrEnd] = {} as any;

            for (let dateOrTime of dateTimeKeys) {
                viewValue[starOrEnd][dateOrTime] = this.pickerService.preSet(
                    dateOrTime,
                    this.INITIAL_VIEW_VALUE[starOrEnd][dateOrTime],
                    this.preSetDayAhead,
                    modelValueToPreSetTo &&
                        <Date>modelValueToPreSetTo[starOrEnd]
                );
            }
        }

        this.viewValue = viewValue;
    }

    onTimePickerModelChange(
        timePickerViewValue,
        dateOrTime,
        viewValueKey?: keyof RuntimePickerViewValue
    ): void {
        this.viewValue[viewValueKey][dateOrTime] =
            this.pickerService.onTimePickerModelChange(timePickerViewValue);
    }
}

angular.module('app').component('pkpRuntimePicker', <
    DateTimePickerComponentOptions
>{
    controller: RuntimePickerComponentCtrl,
    template: require('app/_directives/runtime-picker.component.html'),
    require: {
        ngModelCtrl: 'ngModel'
    },
    bindings: {
        preSetDayAhead: '<',
        startTimeDisabled: '<',
        endTimeDisabled: '<',
        startDateDisabled: '<',
        endDateDisabled: '<',
        minDate: '<',
        maxDate: '<',
        minWidth: '<'
    }
});
